<template>
  <transition name="achievement-fade">
    <div v-if="visible" class="achievement-popup-overlay fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm" @click="dismiss">
      <div
        class="achievement-popup relative bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 transform transition-all duration-300"
        :class="{ 'animating': animating }"
        @click.stop
      >
        <!-- Confetti Background -->
        <div class="confetti-bg absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div class="confetti-piece"></div>
          <div class="confetti-piece"></div>
          <div class="confetti-piece"></div>
          <div class="confetti-piece"></div>
          <div class="confetti-piece"></div>
          <div class="confetti-piece"></div>
        </div>

        <!-- Icon -->
        <div class="achievement-icon text-center mb-4 relative z-10">
          <div class="icon-circle w-24 h-24 mx-auto bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-full flex items-center justify-center text-5xl shadow-lg">
            {{ achievement.icon }}
          </div>
        </div>

        <!-- Type Badge -->
        <div class="achievement-type text-center mb-3 relative z-10">
          <span
            class="type-badge inline-block px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wide"
            :class="achievement.type === 'badge' ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white' : 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white'"
          >
            {{ achievement.type === 'badge' ? 'Badge Unlocked' : 'New Title Earned' }}
          </span>
        </div>

        <!-- Title -->
        <h3 class="achievement-title text-2xl md:text-3xl font-bold text-center text-gray-800 mb-6 relative z-10">
          {{ achievement.title }}
        </h3>

        <!-- Description (if available) -->
        <p v-if="achievement.description" class="achievement-description text-center text-gray-600 mb-4 relative z-10">
          {{ achievement.description }}
        </p>

        <!-- Close Button -->
        <button
          @click="dismiss"
          class="achievement-close absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 transition-colors duration-200 flex items-center justify-center z-20"
          aria-label="Close achievement notification"
        >
          <i class="fas fa-times"></i>
        </button>

        <!-- Auto-dismiss indicator -->
        <div class="auto-dismiss-bar absolute bottom-0 left-0 right-0 h-1 bg-gray-200 rounded-b-2xl overflow-hidden">
          <div class="dismiss-progress h-full bg-gradient-to-r from-purple-500 to-indigo-600" :style="{ width: dismissProgress + '%' }"></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'AchievementPopup',
  props: {
    achievement: {
      type: Object,
      required: true
      // Structure: { type, title, icon, unlocked_at, description }
    }
  },
  emits: ['close'],
  data() {
    return {
      visible: false,
      animating: false,
      dismissProgress: 0,
      dismissTimer: null,
      progressInterval: null
    }
  },
  mounted() {
    // Delay appearance for dramatic effect
    setTimeout(() => {
      this.visible = true
      this.animating = true
    }, 100)

    // Start auto-dismiss progress
    this.startDismissProgress()

    // Auto-dismiss after 5 seconds
    this.dismissTimer = setTimeout(() => {
      this.dismiss()
    }, 5000)
  },
  beforeUnmount() {
    // Cleanup timers
    if (this.dismissTimer) clearTimeout(this.dismissTimer)
    if (this.progressInterval) clearInterval(this.progressInterval)
  },
  methods: {
    /**
     * Dismiss achievement popup
     */
    dismiss() {
      this.animating = false
      setTimeout(() => {
        this.visible = false
        this.$emit('close')
      }, 300)
    },

    /**
     * Start auto-dismiss progress bar animation
     */
    startDismissProgress() {
      const duration = 5000 // 5 seconds
      const interval = 50 // Update every 50ms
      const steps = duration / interval
      let currentStep = 0

      this.progressInterval = setInterval(() => {
        currentStep++
        this.dismissProgress = (currentStep / steps) * 100
        if (currentStep >= steps) {
          clearInterval(this.progressInterval)
        }
      }, interval)
    }
  }
}
</script>

<style scoped>
/* Fade transition */
.achievement-fade-enter-active,
.achievement-fade-leave-active {
  transition: opacity 0.3s ease;
}

.achievement-fade-enter-from,
.achievement-fade-leave-to {
  opacity: 0;
}

/* Popup animation */
.achievement-popup.animating {
  animation: achievement-bounce 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes achievement-bounce {
  0% {
    transform: scale(0) rotate(-180deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.1) rotate(10deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

/* Icon glow animation */
.icon-circle {
  animation: icon-glow 2s ease-in-out infinite;
}

@keyframes icon-glow {
  0%, 100% {
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.5);
  }
  50% {
    box-shadow: 0 0 40px rgba(251, 191, 36, 0.8);
  }
}

/* Confetti pieces */
.confetti-piece {
  position: absolute;
  width: 8px;
  height: 8px;
  background: #f39c12;
  animation: confetti-fall 3s linear infinite;
}

.confetti-piece:nth-child(1) {
  left: 10%;
  animation-delay: 0s;
  background: #e74c3c;
}

.confetti-piece:nth-child(2) {
  left: 25%;
  animation-delay: 0.3s;
  background: #3498db;
}

.confetti-piece:nth-child(3) {
  left: 45%;
  animation-delay: 0.6s;
  background: #2ecc71;
}

.confetti-piece:nth-child(4) {
  left: 60%;
  animation-delay: 0.9s;
  background: #f39c12;
}

.confetti-piece:nth-child(5) {
  left: 75%;
  animation-delay: 1.2s;
  background: #9b59b6;
}

.confetti-piece:nth-child(6) {
  left: 90%;
  animation-delay: 1.5s;
  background: #e67e22;
}

@keyframes confetti-fall {
  0% {
    top: -10%;
    transform: rotate(0deg);
    opacity: 1;
  }
  100% {
    top: 110%;
    transform: rotate(720deg);
    opacity: 0;
  }
}

/* Dismiss progress bar animation */
.dismiss-progress {
  transition: width 50ms linear;
}
</style>
